import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SelectDropdown from "../../../ui/forms/SelectDropdown";
import { getCaseBranches, getLitigationClients } from "../../../API/litigationApis";
import { showToast } from "../../../utils/handleToast";

function AssignmentsFilterModal({ show, close, onApplyFilters, currentFilters = {}, assignmentTypeOptions = [], loadingAssignmentTypes = false }) {
  const [filters, setFilters] = useState({
    client_id: "",
    branch_name: "",
    invoice_number: "",
    lan: "",
    customer_name: "",
    assignment_from_date: "",
    assignment_to_date: "",
    assignment_type: "",
    invoice_status: "",
    ...currentFilters,
  });
  const [clientOptions, setClientOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const applyTimerRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    fetchClients();
  }, [show]);

  useEffect(() => {
    setFilters({
      client_id: "",
      branch_name: "",
      invoice_number: "",
      lan: "",
      customer_name: "",
      assignment_from_date: "",
      assignment_to_date: "",
      assignment_type: "",
      invoice_status: "",
      ...currentFilters,
    });
  }, [currentFilters]);

  useEffect(() => {
    if (!show || !filters.client_id) return;
    fetchBranches(filters.client_id);
  }, [show, filters.client_id]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await getLitigationClients();
      const payload = response?.data || {};
      const rows = Array.isArray(payload?.data?.results)
        ? payload.data.results
        : Array.isArray(payload?.results)
          ? payload.results
          : [];
      const options = Array.isArray(rows)
        ? rows.map((client) => ({
            label: client.full_name || client.name || String(client.id),
            value: client.id,
          }))
        : [];
      setClientOptions(options);
    } catch (error) {
      console.error("Error fetching clients:", error);
      showToast({ variant: "error", message: "Failed to fetch clients" });
      setClientOptions([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchBranches = async (clientId) => {
    if (!clientId) {
      setBranchOptions([]);
      return;
    }

    setLoadingBranches(true);
    try {
      const response = await getCaseBranches({ client_id: clientId });
      const payload = response?.data || {};
      const rows = Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload?.data?.results)
          ? payload.data.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

      const options = Array.isArray(rows)
        ? rows
            .map((branch) => {
              if (typeof branch === "string" || typeof branch === "number") {
                const name = String(branch).trim();
                return name ? { label: name, value: name } : null;
              }

              if (!branch || typeof branch !== "object") return null;

              const nestedBranch = branch.branch || branch.branch_obj || null;
              const branchName =
                branch.branch_name ||
                branch.name ||
                nestedBranch?.branch_name ||
                nestedBranch?.name ||
                "";

              const name = String(branchName).trim();
              if (!name) return null;

              return { label: name, value: name };
            })
            .filter(Boolean)
        : [];

      setBranchOptions(options);
    } catch (error) {
      console.error("Error fetching branches:", error);
      showToast({ variant: "error", message: "Failed to fetch branches" });
      setBranchOptions([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleFilterChange = (field, value) => {
    // Only update state, don't trigger API calls
    if (field === "client") {
      const clientId = value ? Number(value) : "";
      setFilters({
        ...filters,
        client_id: clientId,
        branch_name: "",
      });
      // clear previous branch options; branch list will be fetched
      // by the effect that listens to `filters.client_id`
      setBranchOptions([]);
      return;
    }

    console.log(`Filter Change - Field: ${field}, Value:`, value);
    setFilters({ ...filters, [field]: value });
  };

  const handleApplyFilters = () => {
    const cleanedFilters = {};

    Object.keys(filters).forEach((key) => {
      const val = filters[key];
      if (val !== undefined && val !== null) {
        const str = typeof val === "string" ? val.trim() : val;
        if (str !== "") {
          cleanedFilters[key] = val;
        }
      }
    });

    console.log('Filter Modal - Cleaned Filters:', cleanedFilters);
    console.log('Filter Modal - assignment_type value:', filters.assignment_type);
    // log filters for debugging
    // support async handler: await if onApplyFilters returns a Promise
    try {
      const res = onApplyFilters(cleanedFilters);
      if (res && typeof res.then === "function") {
        res.then(() => close()).catch((err) => {
          console.error("onApplyFilters failed:", err);
          close();
        });
      } else {
        close();
      }
    } catch (err) {
      console.error("onApplyFilters threw:", err);
      close();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      client_id: "",
      branch_name: "",
      invoice_number: "",
      lan_number: "",
      customer_name: "",
      assignment_from_date: "",
      assignment_to_date: "",
      assignment_type: "",
      invoice_status: "",
    });
    setBranchOptions([]);
    onApplyFilters({});
    close();
  };

  return (
    <Modal size="md" show={show} onHide={close} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Filter Assignments</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-0 mt-3">
                <Form.Label className="small">Client</Form.Label>
                <SelectDropdown
                  options={[{ label: "All Clients", value: "" }, ...clientOptions]}
                  placeholder={loadingClients ? "Loading clients..." : "Select client"}
                  value={filters.client_id}
                  onChange={(val) => handleFilterChange("client", val)}
                  disabled={loadingClients}
                  customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0 mt-3">
                <Form.Label className="small">Branch Name</Form.Label>
                <SelectDropdown
                  options={[{ label: "All Branches", value: "" }, ...branchOptions]}
                  placeholder={loadingBranches ? "Loading branches..." : "Select branch"}
                  value={filters.branch_name}
                  onChange={(val) => handleFilterChange("branch_name", val)}
                  disabled={loadingBranches || !filters.client_id}
                  customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Invoice Number</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Enter invoice number"
                  value={filters.invoice_number}
                  onChange={(e) => handleFilterChange("invoice_number", e.target.value)}
                />
              </Form.Group>
            </Col>

           

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Customer Name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Enter customer name"
                  value={filters.customer_name}
                  onChange={(e) => handleFilterChange("customer_name", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Assignment From Date</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.assignment_from_date}
                  onChange={(e) => handleFilterChange("assignment_from_date", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Assignment To Date</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.assignment_to_date}
                  onChange={(e) => handleFilterChange("assignment_to_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">LAN</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Enter LAN"
                  value={filters.lan}
                  onChange={(e) => handleFilterChange("lan", e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Assignment Type</Form.Label>
                <SelectDropdown
                  options={[
                    { label: "All Types", value: "" },
                    ...assignmentTypeOptions.map((type) => ({
                      label: type,
                      value: type,
                    }))
                  ]}
                  placeholder={loadingAssignmentTypes ? "Loading types..." : "Select assignment type"}
                  value={filters.assignment_type}
                  onChange={(selectedValue) => {
                    console.log('SelectDropdown onChange - selectedValue:', selectedValue);
                    handleFilterChange("assignment_type", selectedValue);
                  }}
                  disabled={loadingAssignmentTypes}
                  customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Invoice Status</Form.Label>
                <SelectDropdown
                  options={[
                    { label: "All", value: "" },
                    { label: "Raised", value: "raised" },
                    { label: "Pending", value: "pending" },
                  ]}
                  placeholder="Select status"
                  value={filters.invoice_status}
                  onChange={(val) => handleFilterChange("invoice_status", val)}
                  customStyles={{ control: { padding: "0.15rem 0", fontSize: "0.85rem" } }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClearFilters}>
          Clear All
        </Button>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignmentsFilterModal;
